import * as React from 'react';
import { Button, NotificationBox, Panel } from '@rtkwlf/ui';
import {
  AUTH_ERROR_COOKIE_NAME,
  getCookie,
  RedirectToDefault,
  ROUTE_PATH,
} from '../constants';
import { SessionStorageKey, StatusType } from '../types';
import { withTrashableContext } from '../hooks';
import Viewport from '../widgets/Viewport';
import { useHistory } from 'react-router-dom';

const getLogoutMessage = (): StatusType | null => {
  try {
    return JSON.parse(
      window.sessionStorage.getItem(SessionStorageKey.AUTH_MESSAGE) || '',
    );
  } catch (e) {
    return null;
  }
};

const getErrorMessage = (): string | null => {
  return window.sessionStorage.getItem(SessionStorageKey.LOGIN_ERROR) || '';
};

const getAuth0Error = (): StatusType | null => {
  const reason = getCookie(AUTH_ERROR_COOKIE_NAME) || getErrorMessage();
  if (!reason) {
    return null;
  }

  let message;
  switch (reason) {
    case 'access_denied':
      message =
        'Failed to authenticate: Access denied. Please contact your CST.';
      break;
    case 'lookup_failed':
      message =
        'Failed to authenticate: Service unavailable. Please try again later.';
      break;
    default:
      message =
        'There is a problem with the portal server. Arctic Wolf has been notified. Please try again later.';
  }
  return {
    message,
    color: 'danger',
  };
};

export type LoginStatusProps = {
  showLoginStatus: boolean;
  status: StatusType | null;
  clearStatus: () => void;
};

const clearSavedStatus = () => {
  document.cookie = `${AUTH_ERROR_COOKIE_NAME}=;max-age=0;path=/;`;
  window.sessionStorage.removeItem(SessionStorageKey.AUTH_MESSAGE);
  window.sessionStorage.removeItem(SessionStorageKey.LOGIN_ERROR);
};

export const LoginStatus: React.FC = () => {
  const history = useHistory();
  const handleLogin = () => {
    clearSavedStatus();
    history.push(ROUTE_PATH.ROOT);
  };

  const status = getLogoutMessage() || getAuth0Error();

  if (status) {
    return (
      <Viewport showBrowserAlert={false}>
        <Panel className="login-panel">
          <div className="p-5">
            {status && (
              <div className="d-flex">
                <NotificationBox
                  itemKey="message"
                  notificationType={
                    status.color === 'danger' ? 'error' : status.color
                  }
                  allowClose={false}
                  className="login-error"
                >
                  {status.message}
                </NotificationBox>
              </div>
            )}
          </div>

          <div className="login-panel-footer">
            <Button data-cy="button-login" onClick={handleLogin}>
              Try Log in Again
            </Button>
          </div>
        </Panel>
        <footer className="page-footer">
          &copy; 2022 Arctic Wolf Networks{' '}
          <span className="d-none d-md-inline">Inc. All Rights Reserved.</span>
        </footer>
      </Viewport>
    );
  }

  return <RedirectToDefault />;
};

export default withTrashableContext(LoginStatus);
